import { PageHeader } from '@ant-design/pro-layout'
import { useSuspenseQuery } from '@apollo/client'
import type { ViewFieldsFragment, ViewGroupFieldsFragment } from '@salescore/client-api'
import { FetchViewsDocument, ViewConfigTypeEnum } from '@salescore/client-api'
import { getOrganizationIdFromPath, SuspenseWithLoading } from '@salescore/client-common'
import type { ViewConfigField } from '@salescore/core'
import { getColumnSearchProps } from '@salescore/frontend-common'
import type { ModalFuncProps } from 'antd'
import { App, Button, Col, Divider, Modal, Row, Table } from 'antd'
import { Array, pipe } from 'effect'
import { isNotNullish } from 'effect/Predicate'
import { t } from 'i18next'
import { type ReactElement, useEffect, useMemo, useState } from 'react'
import { useRecoilValue } from 'recoil'

import { changesAtom } from '../../../../recoil/recordsHistory'
import { useGetModelProperty } from '../../../../recoil/view/selectors/connectionsSelector'
import { useSearchViewGroup } from '../../../../recoil/view/selectors/viewsRelatedSelector'
import { useAddFieldsByFieldsMutation } from '../../../../recoil/viewConfig'

export const buildImportFieldsModalProperties = ({
  field,
  setHasChangeToViewSchema,
}: {
  field: ViewConfigField
  setHasChangeToViewSchema: (hasChangeToViewSchema: boolean) => void
}): ModalFuncProps => ({
  icon: null,
  title: <PageHeader title={t(`列をインポート`)} />,
  width: '90%',
  cancelText: t(`閉じる`),
  okButtonProps: { style: { display: 'none' } },
  style: {
    maxWidth: 1000,
  },
  closable: true,
  maskClosable: true,
  destroyOnClose: true,
  footer: null,
  content: (
    <SuspenseWithLoading>
      <ModalContent field={field} setHasChangeToViewSchema={setHasChangeToViewSchema} />
    </SuspenseWithLoading>
  ),
})

const ModalContent = ({
  field,
  setHasChangeToViewSchema,
}: {
  field: ViewConfigField
  setHasChangeToViewSchema: (hasChangeToViewSchema: boolean) => void
}): ReactElement => {
  const changes = useRecoilValue(changesAtom)
  const { message } = App.useApp()

  useEffect(() => {
    if (changes.length > 0) {
      message.warning(t('設定を変更すると変更情報が失われます。'))
    }
  }, [changes.length, message])

  const getModelProperty = useGetModelProperty()
  const searchViewGroup = useSearchViewGroup()
  const {
    data: { views },
  } = useSuspenseQuery(FetchViewsDocument, {
    variables: { organizationId: getOrganizationIdFromPath(), type: ViewConfigTypeEnum.Sheet },
  })
  const viewsWithViewGroup = useMemo(
    () =>
      pipe(
        views,
        Array.flatMapNullishOr((view) => {
          const viewGroup = searchViewGroup(view.viewGroupId)
          if (viewGroup === undefined) {
            return
          }
          const { config } = view
          if (config.type !== 'sheet') {
            return
          }
          const pickableFields = (config.fields ?? [])
            .filter((x) => x.property.modelName === field.property.modelName)
            .map((x) => ({
              ...x,
              name: [x.property.nodeName, x.property.propertyName].join('_'),
            }))
          if (pickableFields.length === 0) {
            return
          }

          return {
            view,
            viewGroup,
            pickableFields,
          }
        }),
      ),
    [field.property.modelName, searchViewGroup, views],
  )
  const [pickableFields, setPickableFields] = useState<Array<ViewConfigField & { name: string }>>([])
  const [pickedFieldNames, setPickedFieldNames] = useState<string[]>([])
  const addByFields = useAddFieldsByFieldsMutation()

  const onFinish = () => {
    const pickedFields = pickedFieldNames
      .map((name) => pickableFields.find((field) => field.name === name))
      .filter(isNotNullish)
    addByFields({
      fields: pickedFields,
      targetField: field,
      onSetConfig: () => {
        setHasChangeToViewSchema(true)
      },
    })
    Modal.destroyAll()
  }

  return (
    <>
      <div className="mb-8">{t(`他のシートから列を一括でインポートすることができます。`)}</div>
      <Row gutter={8} className="">
        <Col span={12}>
          <div className="mb-2 font-bold">1. {t(`インポートするシートを選択`)}</div>
          <Table
            size="small"
            dataSource={viewsWithViewGroup}
            pagination={false}
            columns={[
              {
                key: 'viewGroupName',
                title: t(`フォルダ`),
                ...getColumnSearchProps((record: { viewGroup: ViewGroupFieldsFragment }) => record.viewGroup.name),
                render(_, record) {
                  return <span>{record.viewGroup.name}</span>
                },
              },
              {
                key: 'name',
                title: t(`シート`),
                ...getColumnSearchProps(({ view }: { view: ViewFieldsFragment }) => view.name),
                render(_, record) {
                  return <span>{record.view.name}</span>
                },
              },
              {
                key: 'pick',
                width: 60,
                render(_, record) {
                  return (
                    <Button
                      size="small"
                      onClick={() => {
                        setPickableFields(record.pickableFields)
                        setPickedFieldNames(record.pickableFields.map((x) => x.name))
                      }}
                    >
                      {t(`選択`)}
                    </Button>
                  )
                },
              },
            ]}
          />
        </Col>
        <Col span={1}>
          <Divider type="vertical" className="" style={{ height: '100%' }} />
        </Col>
        <Col span={11}>
          <div className="mb-2 font-bold">2. {t(`インポートする列を選択`)}</div>
          <Table
            size="small"
            pagination={false}
            dataSource={pickableFields.map((x) => ({ ...x, key: x.name }))}
            rowSelection={{
              selectedRowKeys: pickedFieldNames,
              onChange: (newPickedFieldNames) => {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
                setPickedFieldNames(newPickedFieldNames as string[])
              },
            }}
            columns={[
              {
                key: 'metaLabel',
                title: t(`フィールド`),
                render(_, record) {
                  const property = getModelProperty(record.property.modelName, record.property.propertyName)
                  return <span>{property?.label ?? record.property.propertyName}</span>
                },
              },
            ]}
          />
          <div className="mt-4 flex w-full flex-row-reverse">
            <Button
              type="primary"
              disabled={pickedFieldNames.length === 0}
              onClick={() => {
                onFinish()
              }}
            >
              {t(`インポート`)}
            </Button>
          </div>
        </Col>
      </Row>
    </>
  )
}
